import React from "react";
import { Typeahead } from "react-bootstrap-typeahead";
import { Field } from "react-final-form";
import { FormGroup, Label } from "reactstrap";
import { IListOption } from "src/store/models/listOption.models";
import { keyValueToDropdownOption } from "src/util/util.mapDropdownOptions";
import "./TypeaheadFieldV2.scss";

export interface ITypeaheadOption {
  value: string | number | boolean | {};
  label?: string;
}

interface ITypeaheadFieldProps {
  name: string;
  label: string;
  options: ITypeaheadOption[];
  placeholder?: string;
  isClearable?: boolean;
  multiple?: boolean;
  selected?: ITypeaheadOption | ITypeaheadOption[];
  onChange?: (selected: ITypeaheadOption[]) => void;
  className?: string;
}

export const transformOptions = (options: IListOption[] | ITypeaheadOption[]): ITypeaheadOption[] => {
  return options.map(opt => {
    if ("label" in opt && "value" in opt) {
      return opt as ITypeaheadOption;
    }

    return keyValueToDropdownOption(opt as IListOption);
  });
};

const TypeaheadField: React.FC<ITypeaheadFieldProps> = ({
  name,
  label,
  options = [],
  placeholder,
  isClearable = false,
  multiple = false,
  selected,
  onChange,
  className,
}) => {
  const formattedOptions = transformOptions(options);

  return (
    <Field name={name}>
      {({ input, meta }) => {
        const finalSelected = selected
          ? Array.isArray(selected)
            ? selected
            : [selected]
          : multiple && Array.isArray(input.value)
            ? formattedOptions.filter(o => input.value.includes(o.value))
            : formattedOptions.filter(o => o.value === input.value);

        return (
          <FormGroup className={className || "typeahead-form-dropdown"}>
            {label && <Label for={name}>{label}</Label>}
            <Typeahead
              id={name}
              labelKey="label"
              options={formattedOptions}
              selected={finalSelected}
              placeholder={placeholder}
              multiple={multiple}
              clearButton={isClearable}
              onChange={items => {
                if (onChange) {
                  onChange(items.length ? items : undefined);
                } else {
                  if (multiple) {
                    input.onChange(items.map(i => i.value));
                  } else {
                    input.onChange(items[0]?.value ?? null);
                  }
                }
              }}
            />
            {meta.touched && meta.error && (
              <div className="type-ahead-error">
                <span>{meta.error}</span>
              </div>
            )}
          </FormGroup>
        );
      }}
    </Field>
  );
};

export default TypeaheadField;
